import {CommonHttp} from "@/models/interfaces/http/common.http.request";
import {Job, JobInfo, JobsCount, NewJob} from "@/store/jobs";
import {joinQueries} from "@/utils/joinQueries";


export class JobsApi {
    // Получение списка счетчиков "Актуальные - Срочные"
    static getJobsCount(TextFilter?: string, WorkerId?: number, Tags?: number[] | null): Promise<JobsCount> {
        const http = new CommonHttp()
        return http
            .get(`job/count/main${joinQueries({TextFilter, WorkerId, Tags})}`)
            .then(data => data.data)
    }

    // Получение списка работ
    static getJobs(
        TextFilter?: string,
        WorkerId?: number,
        Tags?: number[] | null,
        GetActual?: boolean,
        GetLost?: boolean,
        Count?: number,
        Mode?: number,
        SkipFrom?: string
    ): Promise<Job[]> {
        const http = new CommonHttp()
        return http
            .get(`job/search/main${joinQueries({
                TextFilter,
                WorkerId,
                Tags,
                GetActual,
                GetLost,
                Count,
                Mode,
                SkipFrom
            })}`)
            .then(data => data.data)
    }

    // Получение списка работ для страницы клиента
    static getJobsByClient(
        ClientId: number,
        TextFilter?: string,
        WorkerId?: number,
        Tags?: number[] | null,
        GetActual?: boolean,
        GetLost?: boolean,
        ClientSearch?: boolean,
        HoldingSearch?: boolean,
        Count?: number,
        Mode?: number,
        SkipFrom?: string
    ): Promise<Job[]> {
        const http = new CommonHttp()
        return http
            .get(`job/search/clients${joinQueries({
                ClientId,
                TextFilter,
                WorkerId,
                Tags,
                GetActual,
                GetLost,
                ClientSearch,
                HoldingSearch,
                Count,
                Mode,
                SkipFrom
            })}`)
            .then(data => data.data)
    }

    // Получение списка работ для страницы проекта
    static getJobsByProject(
        ProjectId: number,
        TextFilter?: string,
        WorkerId?: number,
        Tags?: number[] | null,
        GetActual?: boolean,
        GetLost?: boolean,
        Count?: number,
        Mode?: number,
        SkipFrom?: string
    ): Promise<Job[]> {
        const http = new CommonHttp()
        return http
            .get(`job/search/projects${joinQueries({
                ProjectId,
                TextFilter,
                WorkerId,
                Tags,
                GetActual,
                GetLost,
                Count,
                Mode,
                SkipFrom
            })}`)
            .then(data => data.data)
    }

    // Счётчики Actual/Lost для страницы клиента
    static getJobsCountByClient(
        ClientId: number,
        TextFilter?: string,
        WorkerId?: number,
        Tags?: number[] | null,
        ClientSearch?: boolean,
        HoldingSearch?: boolean
    ): Promise<JobsCount> {
        const http = new CommonHttp()
        return http
            .get(`job/count/clients${joinQueries({ClientId, ClientSearch, HoldingSearch, TextFilter, WorkerId, Tags})}`)
            .then(data => data.data)
    }

    // Счётчики Actual/Lost для страницы проекта
    static getJobsCountByProject(
        ProjectId: number,
        TextFilter?: string,
        WorkerId?: number,
        Tags?: number[] | null
    ): Promise<JobsCount> {
        const http = new CommonHttp()
        return http
            .get(`job/count/projects${joinQueries({ProjectId, TextFilter, WorkerId, Tags})}`)
            .then(data => data.data)
    }

    // Создание работы
    static createJob(requestModel: NewJob): Promise<Job[]> {
        const http = new CommonHttp()
        return http
            .post(`job/add`, requestModel)
            .then(data => data.data)
    }

    // Получение списка шаблонов
    static getJobsTemplates(): Promise<Template[]> {
        const http = new CommonHttp()
        return http
            .get(`job/templates`)
            .then(data => data.data)
    }

    // Создание шаблона
    static createJobsTemplate(template: {Name: string, Text: string}): Promise<{Id: number}> {
        const http = new CommonHttp()
        return http
            .put(`job/templates/create`, template)
            .then(data => data.data)
    }

    // Удаление шаблона
    static removeJobsTemplate(templateId: number): Promise<boolean> {
        const http = new CommonHttp()
        return http
            .post(`job/templates/remove/${templateId}`, {})
            .then(res => !!res?.status && res.status >= 200 && res.status < 300)
    }

    static attachFile(
        jobId: number,
        formData: FormData,
        opts?: { onProgress?: (percent: number) => void; signal?: AbortSignal }
    ): Promise<any> {
        const http = new CommonHttp();
        return http
            .post(
                `job/AttachFile/${jobId}/`,
                formData,
                {
                    signal: opts?.signal,
                    onUploadProgress(progressEvent: any) {
                        if (!opts?.onProgress) {
                            return;
                        }
                        if (progressEvent.total) {
                            opts.onProgress(
                                Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            );
                        } else if (typeof progressEvent.progress === 'number') {
                            opts.onProgress(Math.round(progressEvent.progress * 100));
                        }
                    },
                }
            )
            .then((data) => data.data);
    }

    static attachFileFromPojectOrClient(
        jobId: number,
        body: { id: number }
    ): Promise<any> {
        const http = new CommonHttp();
        return http
            .post(`job/AttachFileById/${jobId}/`, body)
            .then((data) => data.data);
    }

    static dettachFile(
        jobId: number,
        body: { id: number }
    ): Promise<any> {
        const http = new CommonHttp();
        return http
            .post(`job/DetachFile/${jobId}/`, body)
            .then((data) => data.data);
    }

    // Исполнитель завершает работу
    static endByWorker(jobId: number, message?: string): Promise<any> {
        const http = new CommonHttp();
        return http
            .patch(`job/end/worker`, { jobId, message })
            .then((data) => data.data);
    }

    // Отправка на проверку
    static sendToChecking(jobId: number, message: string): Promise<any> {
        const http = new CommonHttp();
        return http
            .patch(`job/send/checking`, { jobId, message })
            .then((data) => data.data);
    }

    // Возврат на доработку
    static sendToReworking(jobId: number, message: string): Promise<any> {
        const http = new CommonHttp();
        return http
            .patch(`job/send/reworking`, { jobId, message })
            .then((data) => data.data);
    }

    // Инициатор принимает работу
    static endByCreator(jobId: number): Promise<any> {
        const http = new CommonHttp();
        return http
            .patch(`job/end/creator`, { id: jobId })
            .then((data) => data.data);
    }

    // Админ завершает работу
    static endByAdmin(jobId: number, message?: string): Promise<any> {
        const http = new CommonHttp();
        return http
            .patch(`job/end/admin`, { jobId, message })
            .then((data) => data.data);
    }

    // Удаление работы
    static deleteJob(jobId: number): Promise<any> {
        const http = new CommonHttp();
        return http
            .delete(`job/delete?JobId=${jobId}`)
            .then((data) => data.data);
    }

    // Получение страницы работ "вокруг" указанной
    static getJobsAround(
        JobId: number,
        ClientId?: number,
        ProjectId?: number,
        CountFresh: number = 30,
        CountOld: number = 30
    ): Promise<Job[]> {
        const http = new CommonHttp();
        return http
            .get(`job/around${joinQueries({ JobId, ClientId, ProjectId, CountFresh, CountOld })}`)
            .then((data) => data.data);
    }

    // Редактирование работы
    static editJob(data: { JobId: number, WorkerId?: number, NeedChecking?: boolean, ToDoDate?: string | null, Message?: string, Tags?: string[] }): Promise<any> {
        const http = new CommonHttp();
        return http
            .put(`job/edit`, data)
            .then((data) => data.data);
    }

    // Получение подробной информации о работе (для модалки "Информация о работе")
    static getJobInfo(jobId: number): Promise<JobInfo> {
        const http = new CommonHttp();
        return http
            .get(`job/${jobId}/info`)
            .then((data) => data.data);
    }
}

export type Template = {
    Id: number,
    Name: string,
    Text: string,
}
